import React, { useState, KeyboardEvent } from "react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdownMenu";
import { Button } from "@/components/ui/button";
import Icon from "@/components/icons";

export interface SingleSelectDropdownItem {
  label: string;
  value: string;
  count?: number;
}

interface SingleSelectDropdownMenuProps {
  label: string;
  searchLabel: string;
  items: SingleSelectDropdownItem[];
  selectedValue?: string;
  onSelect: (value: string) => void;
  contentClassName?: string;
  labelFontSize?: string;
  minItemsToShowSearch?: number;
  searchPlaceholder?: string;
  height?: string;
  disabled?: boolean;
}

export const SingleSelectDropdownMenu = ({
  label,
  searchLabel,
  items,
  selectedValue,
  onSelect,
  contentClassName = "",
  labelFontSize = "text-md",
  minItemsToShowSearch = 10,
  searchPlaceholder,
  height = "h-11",
  disabled = false,
}: SingleSelectDropdownMenuProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  const handleItemSelect = (value: string) => {
    onSelect(value);
    setOpen(false);
  };

  const handleClearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect("");
  };

  const filteredItems = items.filter((item) =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const selectedItem = items.find((item) => item.value === selectedValue);

  const isDisabled = disabled || items.length === 0;

  const dynamicSearchLabel = `Search ${searchLabel} (${items.length})`;

  return (
    <DropdownMenu
      modal={false}
      open={open}
      onOpenChange={(next) => !isDisabled && setOpen(next)}
    >
      <DropdownMenuTrigger asChild disabled={isDisabled}>
        <Button
          id={`dropdown-${label.toLowerCase().replace(/\s+/g, "-")}-trigger`}
          variant="primaryOutline"
          disabled={isDisabled}
          className={`font-normal border-2 whitespace-nowrap py-2.5 px-6 ${height} flex items-center justify-between w-full ${isDisabled ? "opacity-50 cursor-not-allowed bg-gray-100" : selectedValue ? "border-[3px] font-semibold bg-primary text-white hover:bg-primary hover:text-white" : "bg-white"}`}
        >
          <span
            className={`p-0 m-0 ${labelFontSize} ${selectedValue ? "" : "hover:text-white"} text-left`}
          >
            {selectedItem
              ? `${selectedItem.label}${selectedItem.count !== undefined ? ` (${selectedItem.count})` : ""}`
              : label}
          </span>
          <div className="flex items-center gap-1">
            {selectedValue && (
              <button
                onClick={handleClearSelection}
                className="flex items-center justify-center hover:opacity-70 transition-opacity"
                aria-label="Clear selection"
              >
                <Icon type="close" />
              </button>
            )}
            <Icon type="keyboard-arrow-down-material" />
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={`mx-2 w-[280px] max-h-[400px] overflow-y-auto scrollbar-thin gap-3 p-0 z-50 ${contentClassName}`}
      >
        {items.length > minItemsToShowSearch && (
          <div className="w-full relative">
            <input
              type="search"
              className="my-2 md:my-0 flex bg-white min-h-12 border-0 border-b border-gray-300 w-full text-sm"
              placeholder={searchPlaceholder || dynamicSearchLabel}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
                e.stopPropagation()
              }
            />
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
              {searchQuery ? (
                <button
                  onClick={handleClearSearch}
                  className="hover:text-gray-600"
                >
                  <Icon type="close" />
                </button>
              ) : (
                <Icon type="manifying-glass" />
              )}
            </div>
          </div>
        )}
        <div className="px-4">
          {filteredItems.map((item) => (
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                handleItemSelect(item.value);
              }}
              key={item.value}
            >
              <div className="py-[10px] w-full">
                <label className="text-gray-700 flex items-center mb-0 leading-5 text-sm cursor-pointer">
                  {item.label}
                  {item.count !== undefined && ` (${item.count})`}
                </label>
              </div>
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
